import React, { useState, useEffect, useCallback, useRef } from 'react';
import NotificationSettings from '../NotificationSettings';
import Portal from '../Portal';
import MenuBody from './MenuBody';

const PANEL_TRANSITION_MS = 200;
const DESKTOP_MQ = '(min-width: 1024px)';

const HamburgerMenu = ({ reduceMotion = false, darkMode, onToggleTheme }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isNotificationSettingsOpen, setIsNotificationSettingsOpen] = useState(false);
  const closeTimerRef = useRef(null);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const openMenu = useCallback(() => {
    clearCloseTimer();
    setIsOpen(true);
  }, [clearCloseTimer]);

  const closeMenu = useCallback(() => {
    clearCloseTimer();
    if (reduceMotion) {
      setIsPanelOpen(false);
      setIsOpen(false);
      return;
    }
    setIsPanelOpen(false);
    closeTimerRef.current = setTimeout(() => {
      setIsOpen(false);
      closeTimerRef.current = null;
    }, PANEL_TRANSITION_MS);
  }, [clearCloseTimer, reduceMotion]);

  const dismissForDesktop = useCallback(() => {
    clearCloseTimer();
    setIsPanelOpen(false);
    setIsOpen(false);
  }, [clearCloseTimer]);

  const handleMenuAction = useCallback((action) => {
    if (action === 'notifications') setIsNotificationSettingsOpen(true);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setIsPanelOpen(false);
      return undefined;
    }

    if (reduceMotion) {
      setIsPanelOpen(true);
      return undefined;
    }

    const frameId = requestAnimationFrame(() => setIsPanelOpen(true));
    return () => cancelAnimationFrame(frameId);
  }, [isOpen, reduceMotion]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') closeMenu();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, closeMenu]);

  useEffect(() => {
    const mq = window.matchMedia(DESKTOP_MQ);
    const onChange = (event) => {
      if (event.matches) dismissForDesktop();
    };

    mq.addEventListener('change', onChange);
    if (mq.matches) dismissForDesktop();

    return () => mq.removeEventListener('change', onChange);
  }, [dismissForDesktop]);

  useEffect(() => () => clearCloseTimer(), [clearCloseTimer]);

  return (
    <>
      <button
        type="button"
        onClick={openMenu}
        className="shell-icon-btn relative active:scale-95 transition-transform duration-100 lg:hidden"
        aria-label="Open menu"
      >
        <svg
          className="w-5 h-5 text-gray-600 dark:text-gray-300"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>

      <Portal>
        {isOpen && (
          <div className="fixed inset-0 z-50 lg:hidden" role="presentation">
            <button
              type="button"
              className="menu-drawer-overlay"
              onClick={closeMenu}
              aria-label="Close menu"
            />

            <div
              className={`menu-drawer-panel ${isPanelOpen ? 'menu-drawer-panel--open' : ''}`}
              role="dialog"
              aria-modal="true"
              aria-label="Site menu"
            >
              {/* Mirrors the site Header bar so the close button lands where the menu button was */}
              <div
                className="flex items-center justify-between pl-5 pr-1 pb-2 border-b border-gray-200/80 dark:border-gray-700/60 flex-shrink-0"
                style={{ paddingTop: 'max(0.5rem, env(safe-area-inset-top, 0px))' }}
              >
                <span className="text-base font-semibold text-gray-900 dark:text-white">
                  Menu
                </span>
                <button
                  type="button"
                  onClick={closeMenu}
                  className="shell-icon-btn flex-shrink-0"
                  aria-label="Close menu"
                >
                  <svg
                    className="w-5 h-5 text-gray-500 dark:text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <MenuBody
                onClose={closeMenu}
                onAction={handleMenuAction}
                darkMode={darkMode}
                onToggleTheme={onToggleTheme}
              />
            </div>
          </div>
        )}
      </Portal>

      <NotificationSettings
        isOpen={isNotificationSettingsOpen}
        onClose={() => setIsNotificationSettingsOpen(false)}
      />
    </>
  );
};

export default HamburgerMenu;
